import { readRuntimeConfig } from '@/lib/api/runtime-config';
import { scrubSentryPayload } from '@/lib/sentryScrub';
import * as Sentry from '@sentry/react';
// Snapshotted at module load (unlike client.ts, which re-reads per call): Sentry.init runs
// once at startup, and the tests rely on this snapshot by re-importing the module per case.
const config = readRuntimeConfig();
/**
 * Cornerstone's dicom-image-loader rejects with the raw XMLHttpRequest (or a
 * `{ error: XMLHttpRequest }` wrapper), and some of its internal promise chains
 * surface those as unhandled rejections that Sentry's global handler captures.
 * Only genuine aborts are dropped (viewer teardown cancels every in-flight
 * prefetch XHR via cache.purgeCache — dozens per abandoned study open, nothing
 * actionable in any of them). Status 0 alone is NOT sufficient: a CORS
 * rejection, DNS/TLS failure, or dropped connection also reports status 0, and
 * those are real outages that must stay visible. The discriminator is
 * readyState: abort() resets a settled XHR to UNSENT (0), while network
 * failures finish at DONE (4). Real image-load failures still reach Sentry
 * through captureViewerError with full viewer context.
 */
export function isAbortedImageFetchRejection(exception) {
    if (!exception || typeof exception !== 'object')
        return false;
    const inner = exception.error ?? exception;
    if (typeof XMLHttpRequest === 'undefined' || !(inner instanceof XMLHttpRequest))
        return false;
    return inner.status === 0 && inner.readyState === XMLHttpRequest.UNSENT;
}
/**
 * Is Sentry actually wired up? Everything Sentry-adjacent (span helpers, tags) is inert
 * without a DSN, which is the local-dev and unit-test case.
 */
export function isSentryEnabled() {
    return !!config.sentryDsn;
}
/** Trace sample rate for everything that is not a viewer route. */
const DEFAULT_TRACES_SAMPLE_RATE = 0.2;
/**
 * The viewer's own trace rate, from `__RUNTIME_CONFIG__.viewerTracesSampleRate` so it can be
 * dialled back at deploy time without a bundle rebuild — a lever worth having, because 1.0
 * is only affordable while viewer traffic is what it is today.
 */
function viewerTracesSampleRate() {
    // Blank is checked BEFORE Number(): `Number('')` is 0, so an injector that renders the
    // field as an empty string — which every non-prod serving path can — would otherwise turn
    // viewer tracing off entirely and look like a deliberate setting. An explicit "0" still
    // means "off"; absent means "default".
    const raw = config.viewerTracesSampleRate?.trim();
    if (!raw)
        return 1.0;
    const configured = Number(raw);
    if (!Number.isFinite(configured) || configured < 0 || configured > 1)
        return 1.0;
    return configured;
}
/**
 * The share-link viewer, `/study/:studyId`. The negative lookahead excludes `/study/list`, the
 * authenticated worklist — the same shape, and for the same reason, as the entry in
 * `lib/publicRoutes.ts`. A bare `/study/` prefix sweeps the worklist in with the viewer, and the
 * worklist is the highest-traffic page in the app: at the viewer rate it is pure oversampling,
 * buying nothing, because pageload and navigation are separate transactions and a sampled
 * worklist pageload does not stitch into the trace of the viewer opened from it.
 */
const SHARED_STUDY_VIEWER = /^\/study\/(?!list$)[^/]+$/;
/**
 * Viewer routes (`/viewer/:id`, and the shared-study viewer at `/study/:id`) are where the
 * slow-load investigations happen, and a flat 0.2 meant four out of five slow reads left no
 * trace at all — including, reliably, the one someone is asking about. They are sampled at
 * their own (higher) rate; everything else keeps the previous flat rate.
 */
export function viewerTracesSampler(context, rates) {
    const path = context.location?.pathname ?? context.name ?? '';
    if (path.startsWith('/viewer/') || SHARED_STUDY_VIEWER.test(path))
        return rates.viewer;
    // Honour an incoming trace's decision where there is one, so a distributed trace is not
    // half-sampled; `inheritOrSampleWith` falls back to the rate given when there isn't.
    return typeof context.inheritOrSampleWith === 'function'
        ? context.inheritOrSampleWith(rates.default)
        : rates.default;
}
export function initSentry() {
    if (!config.sentryDsn)
        return;
    // Tie events to the deployed release so Sentry can flag regressions and
    // "first seen in version X". Empty/whitespace (local dev, workflow_dispatch
    // deploys without a tag) → leave unset rather than tagging a fake release.
    const release = config.appVersion?.trim() ? config.appVersion.trim() : undefined;
    Sentry.init({
        dsn: config.sentryDsn,
        environment: config.environment || 'development',
        release,
        // Replay is NOT listed here: it waits for performance consent and is added by
        // syncSentryReplay(). Its sample rates below still apply once it is.
        integrations: [Sentry.browserTracingIntegration()],
        tracesSampler: (context) => viewerTracesSampler(context, {
            viewer: viewerTracesSampleRate(),
            default: DEFAULT_TRACES_SAMPLE_RATE,
        }),
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 1.0,
        // Share, report-download, reset and invite tokens ride in URLs, which Sentry records on
        // events, breadcrumbs and spans. Scrub all three paths; see lib/sentryScrub.
        beforeSend(event, hint) {
            if (isAbortedImageFetchRejection(hint?.originalException))
                return null;
            return scrubSentryPayload(event);
        },
        beforeSendTransaction: (event) => scrubSentryPayload(event),
        beforeBreadcrumb: (breadcrumb) => scrubSentryPayload(breadcrumb),
    });
}
/** Where Replay keeps its session id; cleared on withdrawal so nothing optional lingers. */
const REPLAY_SESSION_KEY = 'sentryReplaySession';
let replayAdded = false;
let replayRunning = false;
/**
 * Session Replay records the page (text and inputs masked, media blocked: Sentry's defaults) and
 * uploads it when an error occurs. It keeps a session id in sessionStorage, so under the EU cookie
 * rules it runs only with the visitor's performance consent (lib/consent). Called with the
 * current answer at startup and again whenever it changes. Error reporting itself is unaffected.
 */
export function syncSentryReplay(allowed) {
    if (!isSentryEnabled())
        return;
    if (allowed) {
        if (!replayAdded) {
            // Lazily added, it starts itself per replaysOnErrorSampleRate (buffering for errors).
            Sentry.addIntegration(Sentry.replayIntegration());
            replayAdded = true;
        }
        else if (!replayRunning) {
            Sentry.getReplay()?.startBuffering();
        }
        replayRunning = true;
        return;
    }
    if (replayRunning)
        void Sentry.getReplay()?.stop();
    replayRunning = false;
    try {
        sessionStorage.removeItem(REPLAY_SESSION_KEY);
    }
    catch {
        // Storage unavailable: nothing was kept there either.
    }
}
/**
 * Tag every subsequent Sentry event with the machine's WebGL renderer, once per session at
 * viewer first-mount. A viewer running on a software rasteriser behaves nothing like one on
 * a real GPU, and until this tag existed "was the reporter on GPU-fallback rendering?" could
 * not be answered from any recorded signal. Hardware string only — no user, no study.
 */
export function tagSentryDeviceProfile(profile) {
    if (!isSentryEnabled())
        return;
    Sentry.setTag('gpu_renderer', profile.gpu);
}
// Attach (or clear) the signed-in user on every Sentry event, so an issue can
// be traced to the affected user/clinic — "did the paying customer hit this?"
// is the first triage question. Called from CurrentUserProvider.
export function syncSentryUser(user) {
    if (user) {
        Sentry.setUser({ id: user.id, email: user.email });
        Sentry.setTag('clinic_codes', user.clinicRoles.map((cr) => cr.clinicCode).join(','));
    }
    else {
        Sentry.setUser(null);
        Sentry.setTag('clinic_codes', undefined);
    }
}
